import { useCallback, useEffect, useRef, useState } from 'react';
import { extrairCodigo } from '@portal-leo/shared';
import {
  ErroCamera,
  abrirCameraTraseira,
  cameraDisponivel,
  classificarErroCamera,
  pararStream,
} from '@/lib/scanner/camera';
import { obterDetector } from '@/lib/scanner/detector';
import { criarPortaoLeitura } from '@/lib/scanner/portaoLeitura';

interface UseLeitorCodigoOptions {
  ativo: boolean;
  onLeitura: (codigo: string) => void;
}

/**
 * Liga a câmera a um `<video>` e emite os códigos lidos.
 *
 * A vigilância é toda sobre o desmonte: o loop de detecção é assíncrono, então
 * sem a trava de `montado` uma detecção em voo emitiria depois de o modal
 * fechar, adicionando um item ao carrinho pelas costas do caixa.
 */
export function useLeitorCodigo({ ativo, onLeitura }: UseLeitorCodigoOptions) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const portaoRef = useRef(criarPortaoLeitura());
  const onLeituraRef = useRef(onLeitura);

  const [erro, setErro] = useState<ErroCamera | null>(null);
  const [pronto, setPronto] = useState(false);

  // Mantém o callback atual sem reiniciar a câmera a cada render do PDV.
  useEffect(() => {
    onLeituraRef.current = onLeitura;
  }, [onLeitura]);

  useEffect(() => {
    if (!ativo) return;

    let montado = true;
    let frame = 0;

    const parar = () => {
      montado = false;
      if (frame) cancelAnimationFrame(frame);
      pararStream(streamRef.current);
      streamRef.current = null;
      portaoRef.current.limpar();
    };

    const iniciar = async () => {
      setErro(null);
      setPronto(false);

      if (!cameraDisponivel()) {
        setErro(ErroCamera.ContextoInseguro);
        return;
      }

      let detector;
      try {
        detector = await obterDetector();
      } catch {
        if (montado) setErro(ErroCamera.DetectorIndisponivel);
        return;
      }

      let stream: MediaStream;
      try {
        stream = await abrirCameraTraseira((restricoes) =>
          navigator.mediaDevices.getUserMedia(restricoes)
        );
      } catch (e) {
        if (montado) setErro(classificarErroCamera(e));
        return;
      }

      // O modal pode ter fechado enquanto a permissão era decidida.
      if (!montado) {
        pararStream(stream);
        return;
      }

      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        pararStream(stream);
        return;
      }

      video.srcObject = stream;
      await video.play().catch(() => undefined);
      if (montado) setPronto(true);

      const varrer = async () => {
        if (!montado || !videoRef.current) return;

        try {
          const encontrados = await detector.detect(videoRef.current);

          for (const { rawValue } of encontrados) {
            const codigo = extrairCodigo(rawValue);
            if (!codigo) continue;
            if (!portaoRef.current.permitir(codigo, performance.now())) continue;

            if (!montado) return;
            onLeituraRef.current(codigo);
          }
        } catch {
          // Um quadro ruim (vídeo ainda sem dimensão, foco trocando) não é
          // motivo para derrubar o leitor — o próximo quadro tenta de novo.
        }

        if (montado) frame = requestAnimationFrame(() => void varrer());
      };

      frame = requestAnimationFrame(() => void varrer());
    };

    void iniciar();

    return parar;
  }, [ativo]);

  const mensagemErro = erro;

  const reiniciarPortao = useCallback(() => portaoRef.current.limpar(), []);

  return { videoRef, erro: mensagemErro, pronto, reiniciarPortao };
}
